import React from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useIntl } from 'react-intl';
import { FileUploadArea } from 'app/components';
import { FileType } from 'app/components/FileUploadArea/FileUploadArea.types';
import { TranslationKeys } from 'app/translations';
import type { BulkLeftCompanyEmployee } from '../../../Services';
import type { ParseError } from '../types';

type UploadStepProps = {
  csvFile: File | null;
  employees: BulkLeftCompanyEmployee[];
  parseErrors: ParseError[];
  backupError: string | null;
  canLoadPreview: boolean;
  isLoadingBackup: boolean;
  onFileUpload: (files: File[]) => void;
  onLoadPreview: () => void;
  onDownloadTemplate: () => void;
};

export const UploadStep = ({
  csvFile,
  employees,
  parseErrors,
  backupError,
  canLoadPreview,
  isLoadingBackup,
  onFileUpload,
  onLoadPreview,
  onDownloadTemplate,
}: UploadStepProps) => {
  const intl = useIntl();

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_uploadHint })}
        </Typography>
        <Button size="small" variant="outlined" onClick={onDownloadTemplate}>
          {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_downloadTemplate })}
        </Button>
      </Box>

      <FileUploadArea
        maxSize={10 * 1024 * 1024}
        onChange={onFileUpload}
        types={[FileType.Csv]}
        filesLimit={1}
        showSuccessMessage={false}
      />

      {csvFile && (
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          {intl.formatMessage(
            { id: TranslationKeys.adminPanel_bulkLeftCompany_fileSummary },
            { fileName: csvFile.name, count: employees.length },
          )}
        </Typography>
      )}

      {parseErrors.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {parseErrors.map((e, i) => {
            let message: string;
            if (e.kind === 'badHeaders') {
              message = intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_csvError_badHeaders });
            } else if (e.kind === 'badColumnCount') {
              message = intl.formatMessage(
                { id: TranslationKeys.adminPanel_bulkLeftCompany_csvError_badColumnCount },
                { count: e.count },
              );
            } else if (e.kind === 'missingField') {
              message = intl.formatMessage(
                { id: TranslationKeys.adminPanel_bulkLeftCompany_csvError_invalidRow },
                { row: e.row, field: e.field },
              );
            } else if (e.kind === 'invalidUuid') {
              message = intl.formatMessage(
                { id: TranslationKeys.adminPanel_bulkLeftCompany_csvError_invalidUuid },
                { row: e.row, value: e.value },
              );
            } else if (e.kind === 'invalidBsn') {
              message = intl.formatMessage(
                { id: TranslationKeys.adminPanel_bulkLeftCompany_csvError_invalidBsn },
                { row: e.row, value: e.value },
              );
            } else if (e.kind === 'invalidDate') {
              message = intl.formatMessage(
                { id: TranslationKeys.adminPanel_bulkLeftCompany_csvError_invalidDate },
                { row: e.row, value: e.value },
              );
            } else {
              message = intl.formatMessage(
                { id: TranslationKeys.adminPanel_bulkLeftCompany_csvError_invalidStartDate },
                {
                  row: (e as { row: number; value: string }).row,
                  value: (e as { row: number; value: string }).value,
                },
              );
            }
            return (
              <Alert key={`error-${e.kind}-${i}`} severity="error" sx={{ py: 0 }}>
                {message}
              </Alert>
            );
          })}
        </Box>
      )}

      {employees.length === 0 && csvFile && parseErrors.length === 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_csvError_noRows })}
        </Alert>
      )}

      {backupError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {backupError}
        </Alert>
      )}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          disabled={!canLoadPreview}
          endIcon={
            isLoadingBackup ? <CircularProgress size={16} color="inherit" /> : <FontAwesomeIcon icon={faArrowRight} />
          }
          onClick={onLoadPreview}
        >
          {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_uploadPreview })}
        </Button>
      </Box>
    </Box>
  );
};
